import {
  useQuery,
  type QueryFunction,
  type QueryKey,
  type UseQueryDefinedReturnType,
  type UseQueryOptions,
  type UseQueryReturnType
} from '@tanstack/vue-query'
import type {
  DeepUnwrapRef,
  MaybeRef
} from '@tanstack/vue-query/build/lib/types'
import { omit } from 'lodash'
import { watch, type UnwrapRef } from 'vue'

import { errorNotification } from '@/utils/notifications'

export function useFetch<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  queryKey: TQueryKey,
  queryFn: QueryFunction<TQueryFnData, UnwrapRef<TQueryKey>>,
  options?: Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    'queryKey' | 'queryFn' | 'initialData' | 'placeholderData'
  > & {
    errorNotificationText?: string
    showErrorNotification?: boolean
  }
): UseQueryReturnType<TData, TError>

export function useFetch<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  queryKey: TQueryKey,
  queryFn: QueryFunction<TQueryFnData, UnwrapRef<TQueryKey>>,
  options?: Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    'queryKey' | 'queryFn' | 'initialData' | 'placeholderData'
  > & {
    errorNotificationText?: string
    showErrorNotification?: boolean
  } & (
      | { initialData: TQueryFnData | (() => TQueryFnData) }
      | { placeholderData: TQueryFnData | (() => TQueryFnData) }
    )
): UseQueryDefinedReturnType<TData, TError>

export function useFetch<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  queryKey: TQueryKey,
  queryFn: MaybeRef<QueryFunction<TQueryFnData, DeepUnwrapRef<TQueryKey>>>,
  options?: Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    'queryKey' | 'queryFn' | 'initialData'
  > & {
    initialData?: TQueryFnData | (() => TQueryFnData)
    errorNotificationText?: string
    showErrorNotification?: boolean
  }
):
  | UseQueryDefinedReturnType<TData, TError>
  | UseQueryReturnType<TData, TError> {
  const useQueryData = useQuery(queryKey, queryFn, {
    ...omit(options, ['errorNotificationText', 'showErrorNotification'])
  })

  options = {
    ...options,
    showErrorNotification:
      options?.showErrorNotification !== undefined
        ? options?.showErrorNotification
        : true
  }

  watch(
    () => useQueryData.isError.value,
    () => {
      if (useQueryData.isError?.value && options?.showErrorNotification) {
        errorNotification(options?.errorNotificationText)
      }
    },
    { immediate: true }
  )

  if (options?.initialData || options?.placeholderData)
    return useQueryData as UseQueryDefinedReturnType<TData, TError>
  return useQueryData
}
